import { useCallback, useEffect, useState } from 'react';
import type { Procedure, ProcedureAlias, Specialty } from '@starter/shared';
import { useProgramId } from '../../auth/AuthContext.js';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { Modal } from '../../components/Modal.js';
import { PageHeader } from '../../components/PageHeader.js';
import { StatusBadge } from '../../components/StatusBadge.js';

type FormMode = 'create' | 'edit' | null;

export function ProceduresPage() {
  const programId = useProgramId();
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<FormMode>(null);
  const [editing, setEditing] = useState<Procedure | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [aliases, setAliases] = useState<ProcedureAlias[]>([]);
  const [newAlias, setNewAlias] = useState('');

  const [canonicalName, setCanonicalName] = useState('');
  const [defaultSpecialtyId, setDefaultSpecialtyId] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive'>('active');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.listProcedures({ programId, page: 1, pageSize: 100 }),
      api.listSpecialties({ programId, page: 1, pageSize: 100, status: 'active' }),
    ])
      .then(([procRes, specRes]) => {
        setProcedures(procRes.data);
        setSpecialties(specRes.data);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load procedures'))
      .finally(() => setLoading(false));
  }, [programId]);

  useEffect(load, [load]);

  function specialtyName(id: string | null): string {
    if (!id) return '—';
    return specialties.find((s) => s.id === id)?.name ?? '—';
  }

  function openCreate() {
    setEditing(null);
    setCanonicalName('');
    setDefaultSpecialtyId('');
    setStatus('active');
    setAliases([]);
    setNewAlias('');
    setFormError(null);
    setMode('create');
  }

  function openEdit(procedure: Procedure) {
    setEditing(procedure);
    setCanonicalName(procedure.canonicalName);
    setDefaultSpecialtyId(procedure.defaultSpecialtyId ?? '');
    setStatus(procedure.status);
    setAliases([]);
    setNewAlias('');
    setFormError(null);
    setMode('edit');
  }

  function closeModal() {
    setMode(null);
    setEditing(null);
    setFormError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      if (mode === 'create') {
        await api.createProcedure({
          programId,
          canonicalName,
          defaultSpecialtyId: defaultSpecialtyId || null,
        });
      } else if (mode === 'edit' && editing) {
        await api.updateProcedure(editing.id, {
          canonicalName,
          defaultSpecialtyId: defaultSpecialtyId || null,
          status,
        });
      }
      closeModal();
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddAlias() {
    if (!editing || !newAlias.trim()) return;
    setFormError(null);
    try {
      const created = await api.createProcedureAlias(editing.id, { alias: newAlias.trim() });
      setAliases((prev) => [...prev, created]);
      setNewAlias('');
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Failed to add alias');
    }
  }

  async function handleRemoveAlias(alias: ProcedureAlias) {
    if (!editing) return;
    try {
      await api.deleteProcedureAlias(editing.id, alias.id);
      setAliases((prev) => prev.filter((a) => a.id !== alias.id));
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Failed to remove alias');
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="Procedures"
        subtitle="Canonical procedure names and document aliases"
        actions={
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            Add procedure
          </button>
        }
      />

      {error && <ErrorBanner message={error} onRetry={load} />}

      {loading ? (
        <LoadingState message="Loading procedures…" />
      ) : (
        <div className="card">
          {procedures.length === 0 ? (
            <p className="text-muted">No procedures defined yet.</p>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Procedure</th>
                    <th>Default specialty</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {procedures.map((p) => (
                    <tr key={p.id}>
                      <td>{p.canonicalName}</td>
                      <td>{specialtyName(p.defaultSpecialtyId)}</td>
                      <td>
                        <StatusBadge status={p.status} variant={p.status === 'active' ? 'success' : 'muted'} />
                      </td>
                      <td>
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => openEdit(p)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Modal
        open={mode !== null}
        title={mode === 'create' ? 'Add procedure' : 'Edit procedure'}
        onClose={closeModal}
        footer={
          <div className="form-actions">
            <button type="button" className="btn btn--ghost" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" form="procedure-form" className="btn btn--primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        <form id="procedure-form" onSubmit={handleSubmit}>
          {formError && <div className="banner banner--error" role="alert">{formError}</div>}
          <label className="field">
            <span className="field__label">Canonical name</span>
            <input value={canonicalName} onChange={(e) => setCanonicalName(e.target.value)} required />
          </label>
          <label className="field">
            <span className="field__label">Default specialty</span>
            <select value={defaultSpecialtyId} onChange={(e) => setDefaultSpecialtyId(e.target.value)}>
              <option value="">None</option>
              {specialties.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          {mode === 'edit' && (
            <>
              <label className="field">
                <span className="field__label">Status</span>
                <select value={status} onChange={(e) => setStatus(e.target.value as 'active' | 'inactive')}>
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </label>

              <div className="alias-section">
                <h3 className="alias-section__title">Aliases</h3>
                <p className="text-muted text-sm">
                  Alternate spellings from operative reports. Aliases added in this session appear below.
                </p>
                <div className="alias-input-row">
                  <input
                    value={newAlias}
                    onChange={(e) => setNewAlias(e.target.value)}
                    placeholder="e.g. lap chole"
                  />
                  <button type="button" className="btn btn--secondary btn--sm" onClick={() => void handleAddAlias()}>
                    Add
                  </button>
                </div>
                {aliases.length > 0 && (
                  <ul className="alias-list">
                    {aliases.map((a) => (
                      <li key={a.id}>
                        <span>{a.alias}</span>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm btn--danger"
                          onClick={() => void handleRemoveAlias(a)}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </form>
      </Modal>
    </div>
  );
}
